import { useState } from 'react'
import { motion as Motion, useReducedMotion } from 'motion/react'
import { useDashboard } from '../../hooks/useDashboard'
import { TrendingUp, Users, FileCheck, DollarSign, Award, Activity } from 'lucide-react'
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts'

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b']

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.22, ease: 'easeOut' } },
}

const fmtR = (val) => {
  if (!val && val !== 0) return 'R0.00'
  return `R${Number(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const fmtRShort = (val) => {
  const n = Number(val)
  if (n >= 1_000_000) return `R${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `R${(n / 1_000).toFixed(0)}k`
  return `R${n.toFixed(0)}`
}

function MetricCard({ icon: MetricIcon, label, value, subtext, variant = 'default', isDarkMode = false }) {
  const isGrad = variant === 'primary'
  const bg = isGrad
    ? 'bg-gradient-to-br from-sfp-orange to-orange-600 border-transparent'
    : isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
  const text = isGrad ? 'text-white' : isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'
  const label_ = isGrad ? 'text-orange-100' : isDarkMode ? 'text-slate-400' : 'text-gray-500'
  const sub = isGrad ? 'text-orange-50' : isDarkMode ? 'text-slate-400' : 'text-gray-500'
  const icon_ = isGrad ? 'text-orange-100' : 'text-sfp-orange'

  return (
    <Motion.div variants={cardVariants} className={`${bg} rounded-xl p-6 shadow-md border transition-transform hover:scale-[1.02]`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider ${label_}`}>{label}</p>
          <p className={`text-3xl font-bold mt-2 ${text}`}>{value}</p>
          {subtext && <p className={`text-xs mt-1.5 ${sub}`}>{subtext}</p>}
        </div>
        <MetricIcon className={`w-8 h-8 mt-1 ${icon_}`} />
      </div>
    </Motion.div>
  )
}

function ChartCard({ title, icon: ChartIcon, children, isDarkMode }) {
  return (
    <div className={`rounded-xl p-6 shadow-md border transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
    }`}>
      <h2 className={`text-sm font-bold uppercase tracking-wide mb-4 flex items-center gap-2 ${
        isDarkMode ? 'text-slate-300' : 'text-gray-500'
      }`}>
        {ChartIcon && <ChartIcon className="w-4 h-4 text-sfp-orange" />}
        {title}
      </h2>
      {children}
    </div>
  )
}

function EmptyState({ text, isDarkMode }) {
  return (
    <div className={`flex items-center justify-center h-48 text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
      {text}
    </div>
  )
}

const todayStr = () => new Date().toISOString().slice(0, 10)
const yearStartStr = () => `${new Date().getFullYear()}-01-01`

export function Dashboard({ isDarkMode = false }) {
  const [startDate, setStartDate] = useState(yearStartStr())
  const [endDate, setEndDate] = useState(todayStr())

  const {
    totalClients, activePolicies, inactivePolicies,
    advisorCommissions, thisMonthTotal, ytdTotal,
    topClientsByPolicies, monthlyTrend, thisMonthLabel, loading,
  } = useDashboard(startDate, endDate)

  const tickColor = isDarkMode ? '#94a3b8' : '#9ca3af'
  const gridColor = isDarkMode ? '#1e293b' : '#f3f4f6'
  const tooltipStyle = isDarkMode
    ? { background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, fontSize: 12 }
    : { background: '#fff', border: '1px solid #e5e7eb', color: '#1f2937', borderRadius: 8, fontSize: 12 }

  const shouldReduceMotion = useReducedMotion()

  const pieData = advisorCommissions.map(a => ({ name: a.advisor, value: a.amount }))

  return (
    <div className={`p-6 space-y-6 min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-900' : 'bg-sfp-lightgray'
    }`}>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-sfp-darkgray'}`}>
            Key Metrics
          </h1>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            {new Date().toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label htmlFor="dash-start" className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>From</label>
            <input id="dash-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input-field text-sm py-1.5" />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="dash-end" className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>To</label>
            <input id="dash-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input-field text-sm py-1.5" />
          </div>
          <div className="flex gap-1">
            {[
              { label: 'MTD', fn: () => { const d = new Date(); setStartDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); setEndDate(todayStr()) } },
              { label: 'YTD', fn: () => { setStartDate(yearStartStr()); setEndDate(todayStr()) } },
            ].map(b => (
              <button key={b.label} onClick={b.fn} className={`px-2.5 py-1 rounded text-xs font-semibold transition ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{b.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <Motion.div
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: shouldReduceMotion ? 0 : 0.04 } },
        }}
        initial="hidden"
        animate="show"
      >
        <MetricCard
          icon={Users}
          label="Total Clients"
          value={loading ? '—' : totalClients.toLocaleString()}
          subtext="Across all advisors"
          isDarkMode={isDarkMode}
        />
        <MetricCard
          icon={FileCheck}
          label="Active Policies"
          value={loading ? '—' : activePolicies.toLocaleString()}
          subtext={`${inactivePolicies} inactive`}
          isDarkMode={isDarkMode}
        />
        <MetricCard
          icon={DollarSign}
          label="This Month"
          value={loading ? '—' : fmtR(thisMonthTotal)}
          subtext={`Advisor share excl. VAT · ${thisMonthLabel || new Date().toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}`}
          variant="primary"
          isDarkMode={isDarkMode}
        />
        <MetricCard
          icon={TrendingUp}
          label="Period Commission"
          value={loading ? '—' : fmtR(ytdTotal)}
          subtext={`${startDate} → ${endDate} · advisor share excl. VAT`}
          isDarkMode={isDarkMode}
        />
      </Motion.div>

      {/* Charts row 1 — trend + top policies */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

        {/* Commission trend — 3 of 5 cols */}
        <div className="lg:col-span-3">
          <ChartCard title="Commission Trend — Last 6 Months" icon={Activity} isDarkMode={isDarkMode}>
            {loading ? (
              <EmptyState text="Loading…" isDarkMode={isDarkMode} />
            ) : (
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={monthlyTrend} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id="commGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: tickColor }} />
                  <YAxis tick={{ fontSize: 10, fill: tickColor }} tickFormatter={fmtRShort} width={58} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(val) => [fmtR(val), 'Commission']} />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke="#f97316"
                    strokeWidth={2.5}
                    fill="url(#commGrad)"
                    dot={{ r: 4, fill: '#f97316', strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: '#f97316' }}
                    isAnimationActive
                    animationDuration={900}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* Commission donut — 2 of 5 cols */}
        <div className="lg:col-span-2">
          <ChartCard title="Split This Month" icon={DollarSign} isDarkMode={isDarkMode}>
            {loading || pieData.length === 0 ? (
              <EmptyState text={loading ? 'Loading…' : 'No commission this month'} isDarkMode={isDarkMode} />
            ) : (
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                    isAnimationActive
                    animationBegin={150}
                    animationDuration={800}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend
                    iconSize={8}
                    formatter={(v) => <span style={{ color: tickColor, fontSize: 10 }}>{v}</span>}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(val) => [fmtR(val), 'Commission']}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </div>

      {/* Charts row 2 — top advisors bar + top clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Top policies this month — horizontal bar */}
        <ChartCard title={`Top Policies — ${thisMonthLabel || 'This Month'}`} icon={TrendingUp} isDarkMode={isDarkMode}>
          {loading ? (
            <EmptyState text="Loading…" isDarkMode={isDarkMode} />
          ) : advisorCommissions.length === 0 ? (
            <EmptyState text="No commission data this month" isDarkMode={isDarkMode} />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={advisorCommissions}
                layout="vertical"
                margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: tickColor }}
                  tickFormatter={fmtRShort}
                />
                <YAxis
                  type="category"
                  dataKey="advisor"
                  tick={{ fontSize: 10, fill: tickColor }}
                  width={90}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(val) => [fmtR(val), 'Commission']}
                />
                <Bar dataKey="amount" name="Commission" radius={[0, 4, 4, 0]} isAnimationActive animationDuration={700}>
                  {advisorCommissions.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Top clients by policy count */}
        <ChartCard title="Top Clients by Policy Count" icon={Award} isDarkMode={isDarkMode}>
          {loading ? (
            <EmptyState text="Loading…" isDarkMode={isDarkMode} />
          ) : topClientsByPolicies.length === 0 ? (
            <EmptyState text="No client data" isDarkMode={isDarkMode} />
          ) : (
            <div className="space-y-2.5 pt-1">
              {topClientsByPolicies.map((client, idx) => (
                <div
                  key={client.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    idx === 0
                      ? isDarkMode ? 'bg-orange-900/30 border border-orange-700/40' : 'bg-orange-50 border border-orange-200'
                      : isDarkMode ? 'bg-slate-700/40 border border-slate-600/40' : 'bg-gray-50 border border-gray-100'
                  }`}
                >
                  {/* Rank */}
                  <span className={`text-sm font-black w-5 text-center flex-shrink-0 ${
                    idx === 0 ? 'text-sfp-orange' : isDarkMode ? 'text-slate-500' : 'text-gray-400'
                  }`}>
                    {idx + 1}
                  </span>

                  {/* Name + code */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${
                      isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'
                    }`}>
                      {client.full_name || '—'}
                    </p>
                    {client.customer_code && (
                      <p className="text-xs font-mono text-sfp-orange">{client.customer_code}</p>
                    )}
                  </div>

                  {/* Policy count badge */}
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                    idx === 0
                      ? 'bg-sfp-orange text-white'
                      : isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {client.policyCount} {client.policyCount === 1 ? 'policy' : 'policies'}
                  </span>
                </div>
              ))}

              <p className={`text-xs pt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                Clients with the most policies — high value, priority attention
              </p>
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  )
}
